import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
} from '@mui/material';
import { 
  Warning as WarningIcon,
  AccessTime as AccessTimeIcon,
  Logout as LogoutIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

const SessionWarning = () => {
  const { showSessionWarning, extendSession, logout, isAuthenticated, token, user } = useAuth();
  const [countdown, setCountdown] = useState(900); // 15 minutes = 900 seconds

  // Reset countdown when session is extended
  const handleExtendSession = () => {
    setCountdown(900); // Reset to 15 minutes
    extendSession();
  };

  // Countdown timer effect
  useEffect(() => {
    if (!showSessionWarning) {
      setCountdown(900); // Reset countdown when warning is hidden
      return;
    }

    // Reset countdown to 15 minutes when warning appears
    setCountdown(900);

    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [showSessionWarning]);

  // Don't show warning if user is not authenticated or if no token/user
  if (!showSessionWarning || !isAuthenticated || !token || !user) {
    return null;
  }

  // Format countdown time
  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  return (
    <Dialog
      open={showSessionWarning}
      maxWidth="sm"
      disableEscapeKeyDown
      PaperProps={{
        sx: {
          border: '1px solid #ff9800',
          borderRadius: 2,
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 1,
        color: 'warning.main',
        pb: 3
      }}>
        <WarningIcon color="warning" sx={{ fontSize: 28 }} />
        <Typography variant="h6" component="span">
          Session Expiring Soon
        </Typography>
      </DialogTitle>
      
      <DialogContent sx={{ pt: 3 }}>
        <Box sx={{ mb: 1 }}>
          <Alert 
            severity="warning" 
            icon={<AccessTimeIcon />}
            sx={{ mb: 1 }}
          >
            Your session will expire in {formatTime(countdown)}
          </Alert>
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ 
        px: 2, 
        py: 1, 
        gap: 1,
        borderTop: '1px solid #e0e0e0'
      }}>
        <Button
          variant="outlined"
          onClick={async () => await logout()}
          startIcon={<LogoutIcon />}
          color="primary"
        >
          Logout
        </Button>
        
        <Button
          variant="contained"
          onClick={handleExtendSession}
          sx={{ 
            backgroundColor: 'primary.main',
            '&:hover': {
              backgroundColor: 'primary.dark'
            }
          }}
        >
          Extend
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SessionWarning;
